import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import {cn} from '../lib/cn';

export type RadioSize = 'default' | 'm' | 'l';

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({className, ...props}, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn('grid gap-2', className)}
    {...props}
  />
));

RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioSizeClasses: Record<RadioSize, string> = {
  default: 'h-4 w-4',
  m: 'h-5 w-5',
  l: 'h-6 w-6',
};

const indicatorSizeClasses: Record<RadioSize, string> = {
  default: 'h-2 w-2',
  m: 'h-2.5 w-2.5',
  l: 'h-3 w-3',
};

export interface RadioGroupItemProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> {
  size?: RadioSize;
}

export const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({className, size = 'default', ...props}, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      'aspect-square rounded-full border-2 border-[var(--radio-border)] bg-transparent',
      'flex items-center justify-center',
      'focus:outline-none focus-visible:shadow-chekin-focus',
      'disabled:cursor-not-allowed disabled:opacity-50',
      radioSizeClasses[size],
      className,
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator
      className={cn(
        'rounded-full bg-[var(--radio-indicator)]',
        indicatorSizeClasses[size],
      )}
    />
  </RadioGroupPrimitive.Item>
));

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
